import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { PieChart as PieChartIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const COLORS = {
  valid: '#22c55e',
  expiring: '#f59e0b',
  expired: '#ef4444',
  missing: '#6b7280',
};

const STATUS_LABELS = {
  valid: 'Valid',
  expiring: 'Expiring',
  expired: 'Expired',
  missing: 'Missing',
};

// Mock data for simulation
const MOCK_DATA = {
  chartData: [
    { name: 'Valid', key: 'valid', value: 156, color: COLORS.valid },
    { name: 'Expiring', key: 'expiring', value: 23, color: COLORS.expiring },
    { name: 'Expired', key: 'expired', value: 12, color: COLORS.expired },
    { name: 'Missing', key: 'missing', value: 9, color: COLORS.missing },
  ],
  totals: { valid: 156, expiring: 23, expired: 12, missing: 9 },
  total: 200,
};

// Simple SVG Donut Chart component
function DonutChart({ data, size, thickness = 0.3 }) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  const radius = size / 2;
  const innerRadius = radius * (1 - thickness);
  let currentAngle = -90; // Start from top

  const segments = data.map((segment) => {
    const angle = (segment.value / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    // Convert angles to radians
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    // Calculate arc points
    const x1 = radius + radius * Math.cos(startRad);
    const y1 = radius + radius * Math.sin(startRad);
    const x2 = radius + radius * Math.cos(endRad);
    const y2 = radius + radius * Math.sin(endRad);
    const x3 = radius + innerRadius * Math.cos(endRad);
    const y3 = radius + innerRadius * Math.sin(endRad);
    const x4 = radius + innerRadius * Math.cos(startRad);
    const y4 = radius + innerRadius * Math.sin(startRad);

    const largeArc = angle > 180 ? 1 : 0;

    const path = `
      M ${x1} ${y1}
      A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
      L ${x3} ${y3}
      A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
      Z
    `;

    return { ...segment, path };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {segments.map((segment, i) => (
        <path
          key={i}
          d={segment.path}
          fill={segment.color}
          className="transition-opacity hover:opacity-80"
        />
      ))}
    </svg>
  );
}

export function RequirementsByStatusTile({ filter, useMockData }) {
  const { company, userProfile } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id || !userProfile?.id) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const { data: dashData, error } = await supabase.rpc('get_dashboard_data_v2', {
          p_company_id: company.id,
          p_user_id: userProfile.id,
          p_include_archived: false,
          p_limit: 100,
          p_offset: 0,
          p_search: null,
        });

        if (error) throw error;

        let projects = dashData?.projects || [];
        if (filter?.spaceId) {
          projects = projects.filter(p => p.id === filter.spaceId);
        }

        let totals = { valid: 0, expiring: 0, expired: 0, missing: 0 };

        projects.forEach(p => {
          const summary = p.compliance_summary || {};
          totals.valid += summary.valid || 0;
          totals.expiring += summary.expiring || 0;
          totals.expired += summary.expired || 0;
          totals.missing += summary.missing || 0;
        });

        const chartData = [
          { name: 'Valid', key: 'valid', value: totals.valid, color: COLORS.valid },
          { name: 'Expiring', key: 'expiring', value: totals.expiring, color: COLORS.expiring },
          { name: 'Expired', key: 'expired', value: totals.expired, color: COLORS.expired },
          { name: 'Missing', key: 'missing', value: totals.missing, color: COLORS.missing },
        ].filter(d => d.value > 0);

        setData({
          chartData,
          totals,
          total: totals.valid + totals.expiring + totals.expired + totals.missing,
        });
      } catch (err) {
        console.error('Failed to load requirements status:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, userProfile?.id, filter?.spaceId, useMockData]);

  // Determine layout based on dimensions
  const { width, height } = dimensions;
  const isXS = width < 120 || height < 100;
  const isSmall = width < 180 || height < 140;
  const isTall = height > width * 1.3;
  const isWide = width > height * 1.5;
  const isLarge = width >= 300 && height >= 280;

  // Calculate chart size based on available space
  const getChartSize = () => {
    if (isXS) return Math.min(width - 16, height - 30, 70);
    if (isSmall) return Math.min(width - 20, height - 40, 100);
    if (isTall) return Math.min(width - 24, (height - 100) * 0.6, 160);
    if (isWide) return Math.min((width - 120) * 0.5, height - 16, 140);
    if (isLarge) return Math.min(width * 0.55, (height - 140) * 0.8, 180);
    return Math.min(width - 16, height - 50, 140);
  };

  if (loading) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full">
        <div className="animate-pulse">
          <div className="w-16 h-16 rounded-full bg-gray-200" />
        </div>
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full text-gray-400">
        <PieChartIcon className="h-8 w-8 mb-2" />
        <span className="text-sm">No data</span>
      </div>
    );
  }

  const chartSize = getChartSize();
  const compliancePercent = data.total > 0 ? Math.round((data.totals.valid / data.total) * 100) : 0;

  // XS Layout: Just percentage
  if (isXS) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full overflow-visible">
        <div className="text-center">
          <div className={cn(
            'text-2xl font-bold leading-none',
            compliancePercent >= 90 ? 'text-green-600' :
            compliancePercent >= 70 ? 'text-amber-600' : 'text-red-600'
          )}>
            {compliancePercent}%
          </div>
          <p className="text-[10px] text-gray-500 mt-1">status</p>
        </div>
      </div>
    );
  }

  // Small Layout: Mini chart with percentage
  if (isSmall) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full p-2 overflow-visible">
        <div className="relative">
          <DonutChart data={data.chartData} size={chartSize} thickness={0.35} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={cn(
              'text-lg font-bold',
              compliancePercent >= 90 ? 'text-green-600' :
              compliancePercent >= 70 ? 'text-amber-600' : 'text-red-600'
            )}>
              {compliancePercent}%
            </span>
          </div>
        </div>
        <p className="text-[10px] text-gray-500 mt-1">{data.total} total</p>
      </div>
    );
  }

  // Wide Layout: Horizontal arrangement
  if (isWide && !isLarge) {
    return (
      <div ref={containerRef} className="flex items-center h-full gap-3 p-2">
        {/* Chart */}
        <div className="relative shrink-0">
          <DonutChart data={data.chartData} size={chartSize} thickness={0.3} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={cn(
              'text-xl font-bold',
              compliancePercent >= 90 ? 'text-green-600' :
              compliancePercent >= 70 ? 'text-amber-600' : 'text-red-600'
            )}>
              {compliancePercent}%
            </span>
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-1 min-w-0">
          {data.chartData.map((item) => (
            <div key={item.key} className="flex items-center gap-1.5">
              <div
                className="w-2 h-2 rounded-sm shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs text-gray-600 truncate">{item.name}</span>
              <span className="text-xs font-medium ml-auto">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Tall Layout: Vertical stack
  if (isTall) {
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2">
        {/* Chart */}
        <div className="flex-1 flex items-center justify-center">
          <div className="relative">
            <DonutChart data={data.chartData} size={chartSize} thickness={0.3} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <span className={cn(
                  'text-2xl font-bold block',
                  compliancePercent >= 90 ? 'text-green-600' :
                  compliancePercent >= 70 ? 'text-amber-600' : 'text-red-600'
                )}>
                  {compliancePercent}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Vertical legend */}
        <div className="space-y-1 mt-2">
          {data.chartData.map((item) => (
            <div key={item.key} className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-gray-600">{item.name}</span>
              </div>
              <span className="text-xs font-medium">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Large Layout: Full featured
  if (isLarge) {
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2">
        {/* Chart section */}
        <div className="flex-1 flex items-center justify-center min-h-0">
          <div className="relative">
            <DonutChart data={data.chartData} size={chartSize} thickness={0.22} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <span className={cn(
                  'text-4xl font-bold block',
                  compliancePercent >= 90 ? 'text-green-600' :
                  compliancePercent >= 70 ? 'text-amber-600' : 'text-red-600'
                )}>
                  {compliancePercent}%
                </span>
                <span className="text-xs text-gray-500">compliant</span>
              </div>
            </div>
          </div>
        </div>

        {/* Legend grid */}
        <div className="grid grid-cols-2 gap-1.5 mt-2">
          {data.chartData.map((item) => (
            <div key={item.key} className="flex items-center gap-2 p-1.5 rounded" style={{ backgroundColor: `${item.color}15` }}>
              <div
                className="w-3 h-3 rounded-sm shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <div className="min-w-0">
                <span className="text-xs text-gray-600 block">{item.name}</span>
                <span className="text-sm font-semibold">{item.value}</span>
              </div>
              <span className="text-xs text-gray-400 ml-auto">
                {Math.round((item.value / data.total) * 100)}%
              </span>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="mt-2 pt-2 border-t">
          <div className="flex h-1.5 rounded-full overflow-hidden bg-gray-100">
            {data.chartData.map((item) => (
              <div
                key={item.key}
                style={{
                  width: `${(item.value / data.total) * 100}%`,
                  backgroundColor: item.color,
                }}
              />
            ))}
          </div>
          <p className="text-[10px] text-center text-gray-500 mt-1">
            {data.total} total requirements
          </p>
        </div>
      </div>
    );
  }

  // Default/Medium Layout
  return (
    <div ref={containerRef} className="flex flex-col h-full p-2">
      {/* Chart */}
      <div className="flex-1 flex items-center justify-center">
        <div className="relative">
          <DonutChart data={data.chartData} size={chartSize} thickness={0.3} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={cn(
              'text-xl font-bold',
              compliancePercent >= 90 ? 'text-green-600' :
              compliancePercent >= 70 ? 'text-amber-600' : 'text-red-600'
            )}>
              {compliancePercent}%
            </span>
          </div>
        </div>
      </div>

      {/* Horizontal legend */}
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-2">
        {data.chartData.map((item) => (
          <div key={item.key} className="flex items-center gap-1">
            <div
              className="w-2 h-2 rounded-sm shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-[10px] text-gray-600">{item.name}</span>
            <span className="text-[10px] font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
